"use client"

import * as React from "react"
import { Check, ChevronsUpDown } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { FormItem, FormLabel, FormDescription, FormMessage, FormField } from "@/components/ui/form"
import type { FormElement } from "./types"

interface FormComboboxProps {
  element: FormElement
  form: UseFormReturn<any>
}

export function FormCombobox({ element, form }: FormComboboxProps) {
  const [open, setOpen] = React.useState(false)

  // Build options from children
  const options = React.useMemo(() => {
    const opts: Array<{ value: string; label: string; group?: string }> = []

    element.children?.forEach((child) => {
      if (child.type === "option") {
        opts.push({
          value: child.value || "",
          label: child.ui?.text || child.value || "",
        })
      } else if (child.type === "optgroup") {
        const groupLabel = child.attributes?.label
        child.children?.forEach((option) => {
          if (option.type === "option") {
            opts.push({
              value: option.value || "",
              label: option.ui?.text || option.value || "",
              group: groupLabel,
            })
          }
        })
      }
    })

    return opts
  }, [element.children])

  return (
    <FormField
      control={form.control}
      name={element.name!}
      rules={{
        required: element.validation?.required ? `${element.ui?.label || element.name} is required` : false,
      }}
      render={({ field }) => {
        const selectedOption = options.find((option) => option.value === field.value)

        return (
          <FormItem>
            {element.ui?.label && <FormLabel>{element.ui.label}</FormLabel>}
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className={cn("w-full justify-between", !field.value && "text-muted-foreground")}
                >
                  {selectedOption?.label || element.ui?.placeholder || "Select option..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder={`Search ${element.ui?.label?.toLowerCase() || "options"}...`} />
                  <CommandList>
                    <CommandEmpty>No option found.</CommandEmpty>
                    {Array.from(new Set(options.map((o) => o.group).filter(Boolean))).length > 0 ? (
                      Array.from(new Set(options.map((o) => o.group))).map((group) => (
                        <CommandGroup key={group || "default"} heading={group}>
                          {options
                            .filter((opt) => opt.group === group)
                            .map((option) => (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                onSelect={() => {
                                  field.onChange(option.value)
                                  setOpen(false)
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    field.value === option.value ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                {option.label}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      ))
                    ) : (
                      <CommandGroup>
                        {options.map((option) => (
                          <CommandItem
                            key={option.value}
                            value={option.value}
                            onSelect={() => {
                              field.onChange(option.value)
                              setOpen(false)
                            }}
                          >
                            <Check
                              className={cn("mr-2 h-4 w-4", field.value === option.value ? "opacity-100" : "opacity-0")}
                            />
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {element.ui?.hint && <FormDescription>{element.ui.hint}</FormDescription>}
            <FormMessage />
          </FormItem>
        )
      }}
    />
  )
}

